import { CheckboxTable, CheckboxTableHeaderProps, Pagination } from '@dev/ee-components';
import React, { useContext } from 'react';
import { checkIfDataIsLoadedForContactsList, getIsContactsLoading } from '@/store/contacts/actions';
import { differenceWith, isEmpty, isNil } from 'lodash-es';
import { useDispatch, useSelector } from 'react-redux';

import { ApplicationState } from '@/store';
import { DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { addParamToUrl } from '@/services/tools/handleUrlParams';
import { replace } from 'redux-first-history';
import { useContactsCount } from '@/components/Contacts/Group/hooks';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { TableActionButtons } from '../TableActionButtons';
import { ContactTableI } from '../../Interface';
import { ContactTableContext } from '../../context';
import { useHeadersOfContactsListTable, useRowsOfContactsListTable, useSkeletonRows } from '../../hooks';

const columnWidth = {
    addedDate: '130px',
    bounced: '85px',
    clicked: '70px',
    opened: '70px',
    sent: '70px',
    status: '120px',
    updatedDate: '130px',
};

export const DesktopContactTable = ({
    openSingleContactOverlay,
    activePage = 1,
    perPage = DEFAULT_PER_PAGE_COUNT,
}: ContactTableI): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { listname, segmentname } = useParams<{ listname: string; segmentname: string }>();

    const countOfSelectedContacts = useContactsCount();
    const areContactsLoading = useSelector(getIsContactsLoading);
    const { selectedAll, selectedRows, setSelectedRows, setSelectedAll } = useContext(ContactTableContext);
    const headers = useHeadersOfContactsListTable<CheckboxTableHeaderProps[]>();
    const rows = useRowsOfContactsListTable();
    const skeletonRows = useSkeletonRows();

    const { isDataLoaded } = useSelector((store: ApplicationState) =>
        checkIfDataIsLoadedForContactsList(store, listname || segmentname)
    );

    const count = selectedAll ? countOfSelectedContacts : selectedRows?.length;

    const handleSelectedRow = (e, obj: string[], all: boolean) => {
        e.preventDefault();
        // FIXME: we do not have solution for case then we sellect all rows, and then diselect just one, so for now we will only
        const needToResetSelection = isNil(all) && selectedAll;
        const diselectedRow = differenceWith(selectedRows, obj);

        needToResetSelection ? setSelectedRows(diselectedRow) : setSelectedRows(obj);

        setSelectedAll(all || countOfSelectedContacts === selectedRows.length);
    };

    const changePagination = (page: number, perPage: number) => {
        const search = addParamToUrl({ page, perPage });
        dispatch(replace({ search }));
    };

    return (
        <>
            <CheckboxTable
                id="contacts-table"
                columnWidth={columnWidth}
                loading={!isDataLoaded}
                rows={!isDataLoaded ? skeletonRows : rows}
                stickyHeader
                actionList={<TableActionButtons />}
                headers={headers}
                isEmpty={isEmpty(rows) && !areContactsLoading}
                emptyStateProps={{
                    text: t('emptystate'),
                }}
                selectedAll={selectedAll}
                onSelectRow={handleSelectedRow}
                onClickIcon={(e, obj): void => openSingleContactOverlay(obj.email)}
                showHeaderWithActionList={['status']}
                selectedRows={selectedRows}
                totalCount={count}
                rowsTranslations={{
                    firstName: t('firstname'),
                    lastName: t('lastname'),
                    source: t('source'),
                }}
            />
            <Pagination
                perPage={perPage}
                dataLength={countOfSelectedContacts || 0}
                activePage={activePage}
                handlePaginationChange={changePagination}
            />
        </>
    );
};
